import {
  IDentalChart,
  IDentalData,
  IPerioChart,
  IPerioChartData,
  PerioChartSectionData,
} from '@dataTypes/chartingFeature.types'

export type ChartSaveSource = 'autosave' | 'manual'
export type ChartAutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error'

const PERIO_SECTION_KEYS: Array<keyof PerioChartSectionData> = [
  'PD',
  'GM',
  'CAL',
  'Bleeding',
  'Calculus',
  'Supp',
  'Furcation',
  'Mobility',
]

export function hasDentalChartableContent(dentalData?: IDentalData[]): boolean {
  return (dentalData?.length ?? 0) > 0
}

/** Identity for a diagnostic row: tooth + condition + type + surface. */
export function dentalConditionIdentity(item: IDentalData): string {
  return [
    String(item.tooth_number ?? '').trim(),
    (item.condition ?? '').toLowerCase(),
    (item.type ?? '').toLowerCase(),
    (item.surface ?? '').toLowerCase(),
  ].join('|')
}

/** True when `next` has at least one diagnostic code that was not in `previous`. */
export function hasNewDentalConditions(
  previous: IDentalData[] | undefined,
  next: IDentalData[] | undefined,
): boolean {
  const previousKeys = new Set((previous ?? []).map(dentalConditionIdentity))
  return (next ?? []).some(
    (item) => !previousKeys.has(dentalConditionIdentity(item)),
  )
}

export function hasPerioSectionValues(
  section?: PerioChartSectionData,
): boolean {
  if (!section) return false
  return PERIO_SECTION_KEYS.some((key) => {
    const values = section[key]
    if (!Array.isArray(values)) return false
    return values.some((value) => String(value ?? '').trim() !== '')
  })
}

export function hasPerioChartableContent(perioData?: IPerioChartData): boolean {
  if (!perioData) return false
  return (
    hasPerioSectionValues(perioData.facial) ||
    hasPerioSectionValues(perioData.lingual)
  )
}

export function buildDentalChartSignature(chart: IDentalChart): string {
  return JSON.stringify({
    chart_id: chart.chart_id ?? '',
    patient_id: chart.patient_id ?? '',
    provider_id: chart.provider_id ?? '',
    clinic_id: chart.clinic_id ?? '',
    title: chart.title ?? '',
    dental_data: chart.dental_data ?? [],
  })
}

export function buildPerioChartSignature(chart: IPerioChart): string {
  return JSON.stringify({
    chart_id: chart.chart_id ?? '',
    patient_id: chart.patient_id ?? '',
    provider_id: chart.provider_id ?? '',
    clinic_id: chart.clinic_id ?? '',
    title: chart.title ?? '',
    perio_data: chart.perio_data,
  })
}

export function mergeSavedDentalChart(
  current: IDentalChart,
  saved: IDentalChart,
): IDentalChart {
  return {
    ...current,
    chart_id: saved.chart_id,
    title: saved.title || current.title,
    provider_id: saved.provider_id ?? current.provider_id,
    provider: saved.provider ?? current.provider,
    patient_id: saved.patient_id || current.patient_id,
    clinic_id: saved.clinic_id || current.clinic_id,
    dental_data: saved.dental_data ?? current.dental_data,
    description: saved.description ?? current.description,
    date_time_created_at: saved.date_time_created_at,
    date_time_updated_at: saved.date_time_updated_at,
  }
}

export function mergeSavedPerioChart(
  current: IPerioChart,
  saved: IPerioChart,
): IPerioChart {
  return {
    ...current,
    chart_id: saved.chart_id,
    title: saved.title || current.title,
    provider_id: saved.provider_id ?? current.provider_id,
    provider: saved.provider ?? current.provider,
    patient_id: saved.patient_id || current.patient_id,
    clinic_id: saved.clinic_id || current.clinic_id,
    perio_data: saved.perio_data ?? current.perio_data,
    description: saved.description ?? current.description,
    date_time_created_at: saved.date_time_created_at,
    date_time_updated_at: saved.date_time_updated_at,
  }
}

export interface ReconciledPerioChart {
  chart: IPerioChart
  hasConcurrentChanges: boolean
}

const valuesMatch = (left: unknown, right: unknown): boolean =>
  JSON.stringify(left) === JSON.stringify(right)

const reconcileClientValue = <T>(
  requestValue: T,
  latestValue: T,
  serverValue: T,
): T =>
  valuesMatch(latestValue, requestValue) && serverValue !== undefined
    ? serverValue
    : latestValue

function reconcilePerioSection(
  request: PerioChartSectionData,
  latest: PerioChartSectionData,
  server: PerioChartSectionData,
): PerioChartSectionData {
  return PERIO_SECTION_KEYS.reduce<PerioChartSectionData>(
    (section, key) => {
      const length = Math.max(
        request[key].length,
        latest[key].length,
        server[key].length,
      )
      section[key] = Array.from({ length }, (_, index) =>
        reconcileClientValue(
          request[key][index],
          latest[key][index],
          server[key][index],
        ),
      )
      return section
    },
    {
      PD: [],
      GM: [],
      CAL: [],
      Bleeding: [],
      Calculus: [],
      Supp: [],
      Furcation: [],
      Mobility: [],
    },
  )
}

const editablePerioValues = (chart: IPerioChart): object => ({
  patient_id: chart.patient_id,
  provider_id: chart.provider_id,
  clinic_id: chart.clinic_id,
  title: chart.title,
  description: chart.description,
  perio_data: chart.perio_data,
})

export function reconcileSavedPerioChart(
  request: IPerioChart,
  latest: IPerioChart,
  server: IPerioChart,
): ReconciledPerioChart {
  const chart: IPerioChart = {
    ...latest,
    chart_id: server.chart_id ?? latest.chart_id,
    patient_id: reconcileClientValue(
      request.patient_id,
      latest.patient_id,
      server.patient_id,
    ),
    provider_id: reconcileClientValue(
      request.provider_id,
      latest.provider_id,
      server.provider_id,
    ),
    clinic_id: reconcileClientValue(
      request.clinic_id,
      latest.clinic_id,
      server.clinic_id,
    ),
    title: reconcileClientValue(request.title, latest.title, server.title),
    description: reconcileClientValue(
      request.description,
      latest.description,
      server.description,
    ),
    perio_data: {
      facial: reconcilePerioSection(
        request.perio_data.facial,
        latest.perio_data.facial,
        server.perio_data.facial,
      ),
      lingual: reconcilePerioSection(
        request.perio_data.lingual,
        latest.perio_data.lingual,
        server.perio_data.lingual,
      ),
    },
    patient: server.patient ?? latest.patient,
    provider: server.provider ?? latest.provider,
    notes: server.notes ?? latest.notes,
    created_by: server.created_by ?? latest.created_by,
    updated_by: server.updated_by ?? latest.updated_by,
    date_time_created_at:
      server.date_time_created_at ?? latest.date_time_created_at,
    date_time_updated_at:
      server.date_time_updated_at ?? latest.date_time_updated_at,
  }
  return {
    chart,
    hasConcurrentChanges: !valuesMatch(
      editablePerioValues(request),
      editablePerioValues(latest),
    ),
  }
}

export interface UniqueChartTitleResolution {
  requestedTitle: string
  uniqueTitle: string
  wasRewritten: boolean
}

export type ChartTitleLookup = {
  chart_id?: string
  patient_id?: string
  title?: string
}

export function resolveUniqueChartTitle(
  baseTitle: string,
  existingTitles: string[],
): string {
  const normalizedTaken = new Set(
    existingTitles.map((title) => title.trim().toLowerCase()).filter(Boolean),
  )
  const trimmedBase = baseTitle.trim()
  if (!trimmedBase) {
    return trimmedBase
  }
  if (!normalizedTaken.has(trimmedBase.toLowerCase())) {
    return trimmedBase
  }
  const suffixMatch = trimmedBase.match(/^(.*)\s+\((\d+)\)$/)
  const rootTitle = suffixMatch?.[1]?.trim() || trimmedBase
  let suffix = suffixMatch ? Number(suffixMatch[2]) + 1 : 2
  while (normalizedTaken.has(`${rootTitle} (${suffix})`.toLowerCase())) {
    suffix += 1
  }
  return `${rootTitle} (${suffix})`
}

export function resolveUniqueTitleAmongCharts(
  title: string,
  charts: ChartTitleLookup[],
  patientId: string,
  excludeChartId = '',
): string {
  return resolveUniqueChartTitle(
    title,
    charts
      .filter(
        (chart) =>
          (chart.patient_id ?? '') === patientId &&
          (excludeChartId === '' || chart.chart_id !== excludeChartId),
      )
      .map((chart) => chart.title ?? ''),
  )
}

export function getUniqueChartTitleResolution(
  title: string,
  charts: ChartTitleLookup[],
  patientId: string,
  excludeChartId = '',
): UniqueChartTitleResolution {
  const requestedTitle = title.trim()
  const uniqueTitle = resolveUniqueTitleAmongCharts(
    title,
    charts,
    patientId,
    excludeChartId,
  )
  return {
    requestedTitle,
    uniqueTitle,
    wasRewritten: requestedTitle !== '' && requestedTitle !== uniqueTitle,
  }
}
